import React, { FC, useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { useToasts } from 'react-toast-notifications';

import { Button } from 'components/UtilElements';
import { apiErrorHandler, toastHookToContext } from 'core/errors';
import { Dispatch } from 'redux';
import { UPDATE_COURSE_TIMELINE, UPDATE_ENROLLMENTS } from 'redux/actions';
import { API, request } from 'redux/api';
import { RootState } from 'redux/reducers';

import CreateClassDialog from './CreateClassDialog';
import TimelineWidget from './TimelineWidget';

interface SaveCourseTimelineResponse {
  message: string;
  enrolments: {
    students: CourseUser[];
    importStatus: ImportStatus;
    totalStudents: number;
    studentsImported: number;
  };
}

const toRawCourseTimelineClass = (t: CourseTimelineClass): RawCourseTimelineClass => ({
  id: t.id,
  startTime: t.startTime,
  startTimeUnix: t.startTimeUnix,
  endTime: t.endTime,
  endTimeUnix: t.endTimeUnix,
  venue: t.venue,
  isOnlineMeeting: t.isOnlineMeeting,
});

const saveCourseTimeline = (
  courseId: string,
  newClasses: CourseTimelineClass[],
  removedClasses: CourseTimelineClass[]
) => {
  return request<SaveCourseTimelineResponse>({
    method: 'PATCH',
    url: API.COURSE_CREATE_SAVE_TIMELINE,
    data: { courseId, newClasses, removedClasses },
  });
};

interface CourseTimelineProps {
  course: CourseDetails | null;
  timeline: CourseTimelineClass[];
  onPrev(): void;
  onNext(): void;
  updateTimeline(data: CourseTimelineClass[]): Promise<void>;
  updateEnrollments(data: CourseEnrollments): Promise<void>;
}

const CourseTimeline: FC<CourseTimelineProps> = ({
  course,
  timeline,
  onPrev,
  onNext,
  updateTimeline,
  updateEnrollments,
}) => {
  const tm = useToasts();
  const [showAddClassDialog, setShowAddClassDialog] = useState(false);

  const onClassToggle = useCallback(
    (id: string) =>
      updateTimeline(timeline.map((c) => (c.id === id ? { ...c, isRemoved: !c.isRemoved } : c))),
    [timeline, updateTimeline]
  );

  const onCloseAddClassDialog = useCallback(
    (newClass?: CourseTimelineClass) => {
      /**
       * newClass can be CourseTimelineClass object or SyntheticBaseEvent
       * so this condition narrow down to CourseTimelineClass
       */
      if (newClass && typeof newClass === 'object' && 'id' in newClass) {
        const isDuplicate = timeline.find((item) => item.id === newClass?.id);
        if (newClass && !isDuplicate) {
          const updatedTimeline = timeline.concat(newClass).sort((t1, t2) => {
            if (t2.id > t1.id) return -1;
            if (t2.id < t1.id) return 1;
            return 0;
          });
          updateTimeline(updatedTimeline);
        }
      }

      setShowAddClassDialog(false);
    },
    [timeline, updateTimeline]
  );

  const handleGoBack = useCallback(() => {
    onPrev();
  }, [onPrev]);

  const onSubmit = useCallback(async () => {
    if (!course) return;
    try {
      const newClasses = timeline.filter((t) => t.isNew && !t.isRemoved).map(toRawCourseTimelineClass);
      const removedClasses = timeline.filter((t) => !t.isNew && t.isRemoved).map(toRawCourseTimelineClass);
      const { enrolments } = await saveCourseTimeline(course.courseId, newClasses, removedClasses);
      updateEnrollments(enrolments);
      onNext();
    } catch (err: any) {
      apiErrorHandler(toastHookToContext(tm), err);
    }
  }, [course, onNext, timeline, tm, updateEnrollments]);

  return (
    <>
      <form className='import-course__form with-actions'>
        <div className='import-course__form-content'>
          <label className='import-course__label'>Timeline</label>
          {timeline.map((t) => (
            <TimelineWidget key={t.id} {...t} onClassToggle={onClassToggle} />
          ))}
        </div>
        <div className='import-course__form-actions'>
          <Button
            variant='contained'
            color='info'
            size='large'
            fullWidth
            onClick={() => setShowAddClassDialog(true)}
          >
            Add a Class
          </Button>
        </div>
      </form>
      <section className='import-course__info'>
        <h2 className='import-course__title'>Step 3: Curate course timeline</h2>
        <p className='import-course__description'>
          You can add or remove classes that do not follow the normal weekly schedule
        </p>
        <div className='import-course__actions'>
          <div className='import-course__action'>
            <Button
              preventMinWidth
              className='import-course__action__back'
              variant='text'
              color='info'
              size='large'
              onClick={handleGoBack}
            >
              Back
            </Button>
          </div>
          <div className='import-course__action'>
            <Button variant='contained' color='success' size='large' onClick={onSubmit}>
              Save and Continue
            </Button>
          </div>
        </div>
      </section>
      <CreateClassDialog course={course} show={showAddClassDialog} onClose={onCloseAddClassDialog} />
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  course: state.courseInCreation,
  timeline: state.timeline,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  updateTimeline: async (data: CourseTimelineClass[]) => {
    dispatch({
      type: UPDATE_COURSE_TIMELINE,
      payload: data,
    });
  },
  updateEnrollments: async (data: CourseEnrollments) => {
    dispatch({
      type: UPDATE_ENROLLMENTS,
      payload: data,
    });
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(CourseTimeline);
